// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Forge Casino — Player Profile
// Settings, session history, responsible gaming shortcuts
// ================================================================
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useCasino } from '../context/CasinoShell';

export default function PlayerProfile() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { forgeBalance, kycStatus, sessionStats, sessionLimits, getSessionDuration, fairnessLog } = useCasino();

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>⚙️ PROFILE</h1>

      {/* Player Card */}
      <div style={styles.playerCard}>
        <div style={styles.avatar}>{user?.full_name?.charAt(0) || 'P'}</div>
        <div>
          <h2 style={styles.playerName}>{user?.full_name || user?.email || 'Player'}</h2>
          <p style={styles.playerEmail}>{user?.email}</p>
          <div style={styles.badges}>
            <span style={styles.badge}>{kycStatus === 'verified' ? '✅ KYC Verified' : '⏳ KYC Pending'}</span>
            <span style={styles.badge}>18+</span>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div style={styles.statsGrid}>
        <div style={styles.stat}>
          <span style={styles.statLabel}>BALANCE</span>
          <span style={{ ...styles.statValue, color: '#FFD700' }}>${forgeBalance.toFixed(2)}</span>
        </div>
        <div style={styles.stat}>
          <span style={styles.statLabel}>SESSION TIME</span>
          <span style={styles.statValue}>{getSessionDuration()}m</span>
        </div>
        <div style={styles.stat}>
          <span style={styles.statLabel}>TOTAL SPINS</span>
          <span style={styles.statValue}>{sessionStats.spinsThisSession}</span>
        </div>
        <div style={styles.stat}>
          <span style={styles.statLabel}>FAIRNESS PROOFS</span>
          <span style={{ ...styles.statValue, color: '#00FF88' }}>{fairnessLog.length}</span>
        </div>
      </div>

      {/* Active Limits */}
      <div style={styles.limitsCard}>
        <h3 style={styles.sectionTitle}>ACTIVE LIMITS</h3>
        <div style={styles.limitsList}>
          <div style={styles.limitRow}>
            <span>Daily Deposit Limit</span>
            <span style={styles.limitValue}>{sessionLimits.depositDaily ? `$${sessionLimits.depositDaily}` : 'None'}</span>
          </div>
          <div style={styles.limitRow}>
            <span>Session Loss Limit</span>
            <span style={styles.limitValue}>{sessionLimits.lossSession ? `$${sessionLimits.lossSession}` : 'None'}</span>
          </div>
          <div style={styles.limitRow}>
            <span>Time Limit</span>
            <span style={styles.limitValue}>{sessionLimits.timeSession ? `${sessionLimits.timeSession}min` : 'None'}</span>
          </div>
          <div style={styles.limitRow}>
            <span>Reality Check</span>
            <span style={styles.limitValue}>{sessionLimits.realityCheck ? `${sessionLimits.realityCheck}min` : 'Off'}</span>
          </div>
        </div>
        <button style={styles.limitsBtn} onClick={() => navigate('/responsible')}>
          MANAGE LIMITS
        </button>
      </div>

      {/* Quick Actions */}
      <div style={styles.actions}>
        <button style={styles.actionBtn} onClick={() => navigate('/vault')}>💰 Wallet</button>
        <button style={styles.actionBtn} onClick={() => navigate('/vault/history')}>📋 History</button>
        <button style={styles.actionBtn} onClick={() => navigate('/vault/verify')}>✅ Verify Spins</button>
        <button style={styles.actionBtn} onClick={() => navigate('/kyc')}>🛡️ KYC</button>
        <button style={styles.actionBtn} onClick={() => navigate('/responsible')}>🛑 Responsible Gaming</button>
        <button style={{ ...styles.actionBtn, borderColor: '#DC262666', color: '#DC2626' }} onClick={() => { signOut(); navigate('/login'); }}>
          Logout
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: { padding: 16, maxWidth: 560, margin: '0 auto', minHeight: '100vh', background: '#0a0a0a' },
  title: { fontSize: 20, fontWeight: 900, color: '#fff', letterSpacing: 2, marginBottom: 16 },
  playerCard: {
    display: 'flex', gap: 16, padding: 16, background: '#111', borderRadius: 12,
    border: '1px solid #222', marginBottom: 16, alignItems: 'center',
  },
  avatar: {
    width: 56, height: 56, borderRadius: '50%', background: '#DC2626', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 24, fontWeight: 900, flexShrink: 0,
  },
  playerName: { fontSize: 16, color: '#fff', fontWeight: 900, margin: '0 0 4px' },
  playerEmail: { fontSize: 11, color: '#666', margin: '0 0 8px' },
  badges: { display: 'flex', gap: 6 },
  badge: { padding: '2px 8px', borderRadius: 4, background: '#1a1a1a', border: '1px solid #333', fontSize: 9, color: '#888', fontWeight: 700 },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 16 },
  stat: { padding: 12, background: '#111', borderRadius: 8, border: '1px solid #222', textAlign: 'center' },
  statLabel: { display: 'block', fontSize: 8, color: '#555', fontWeight: 700, letterSpacing: 1, marginBottom: 4 },
  statValue: { fontSize: 18, fontWeight: 900, color: '#fff', fontFamily: "'Courier New', monospace" },
  limitsCard: { padding: 16, background: '#111', borderRadius: 12, border: '1px solid #222', marginBottom: 16 },
  sectionTitle: { fontSize: 12, color: '#DC2626', fontWeight: 900, letterSpacing: 2, marginBottom: 12, marginTop: 0 },
  limitsList: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 },
  limitRow: { display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#ccc', padding: '4px 0' },
  limitValue: { color: '#FFD700', fontWeight: 700 },
  limitsBtn: {
    width: '100%', padding: 8, borderRadius: 6, border: '1px solid #DC2626',
    background: '#DC262222', color: '#DC2626', fontSize: 10, fontWeight: 700, cursor: 'pointer',
  },
  actions: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 },
  actionBtn: {
    padding: '12px 8px', borderRadius: 8, border: '1px solid #222', background: '#111',
    color: '#ccc', fontSize: 11, fontWeight: 700, cursor: 'pointer', textAlign: 'center',
  },
};
